import { z } from 'zod/v3';

const runSchema = z.object({
  type: z.literal('run'),
  runKey: z.string(),
  frameKey: z.string(),
  id: z.string(),
  status: z.enum(['pending', 'completed', 'failed']),
  result: z.any(),
  startAt: z.date().nullable(),
  endAt: z.date().nullable(),
});

const group = z.object({
  type: z.literal('group'),
  runKey: z.string(),
  status: z.enum(['pending', 'completed', 'failed', 'none']),
  stats: z.object({
    startAt: z.date().nullable(),
    endAt: z.date().nullable(),
    total: z.number(),
    pending: z.number(),
    completed: z.number(),
    failed: z.number(),
  }),
});

const runOrGroupStats = z.discriminatedUnion('type', [runSchema, group]);

export type StackFrameRunOrGroup = z.infer<typeof runOrGroupStats>;

export const ATCStackFrameStatusSchema = z.enum([
  'pending',
  'running',
  'completed',
  'failed',
  'cancelled',
  'waiting',
  'queued',
  'allocated',
  'stalled',
]);

export type ATCStackFrameStatus = z.infer<typeof ATCStackFrameStatusSchema>;

const stackFrameEventSchema = z.object({
  id: z.string(),
  eventType: z.enum([
    'start',
    'success',
    'fail',
    'error',
    'retry',
    'cancel',
    'queued',
    'allocated',
  ]),
  createdAt: z.date(),
  metadataId: z.string().nullable(),
});

const stackFrameSchema = z.object({
  id: z.string(),
  parentId: z.string().nullable(),
  status: ATCStackFrameStatusSchema,
  createdAt: z.date(),
  depth: z.number(),
  position: z.number(),
  input: z.any().nullable(),
  output: z.record(z.string(), z.any()).nullable(),
  fpType: z.string(),
  displayName: z.string(),
  tags: z.array(z.string()),
  retries: z.number(),
  events: z.array(stackFrameEventSchema),
  startedAt: z.date().nullable(),
  endedAt: z.date().nullable(),
  allocatedAt: z.date().nullable(),
  cancelledAt: z.date().nullable(),
});

export type StackFrameType = z.infer<typeof stackFrameSchema>;

const getStackFramesForRunKeyResponseSchema = z.object({
  runId: z.string(),
  orgId: z.string(),
  runKey: z.string(),
  tags: z.array(z.string()),
  heartbeatAt: z.date().nullable(),
  uddWriteKey: z.any(),
  frames: z.array(stackFrameSchema),
});

export type GetStackFramesForRunKeyResponse = z.infer<
  typeof getStackFramesForRunKeyResponseSchema
>;

/** Slim schema for data-grid cell preview polling - only fields needed by the UI */
const stackFramePreviewSchema = z.object({
  id: z.string(),
  status: ATCStackFrameStatusSchema,
  depth: z.number(),
  position: z.number(),
  output: z.record(z.string(), z.any()).nullable(),
  fpType: z.string(),
  displayName: z.string(),
});

const getStackFramesForRunKeyPreviewResponseSchema = z.object({
  frame: stackFramePreviewSchema.nullable(),
  allComplete: z.boolean(),
});

export type GetStackFramesForRunKeyPreviewResponse = z.infer<
  typeof getStackFramesForRunKeyPreviewResponseSchema
>;
